import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  getWorkflowPlan,
  WorkflowDetail,
  WorkflowJob,
  WorkflowPlan,
  WorkflowPlanSpec,
} from "../../api/workflows";
import { MeResponse } from "../../api/me";
import { estimateJobEta, formatDuration } from "../../api/workflowSummary";
import { costForJob, formatCost } from "../../api/cost";

// Per-job progress table. One row per Job, paired against the
// workflow plan so the user sees every month from t=0 instead of
// watching them appear as concurrency slots free up. Planned rows
// (specs without an actual job row yet) render with status="planned"
// and an empty progress / cost column.
//
// Sorted by month ascending so the timeline reads top-to-bottom in
// time order. Default-collapses to the first 25 rows once we cross
// 40 total; the user can expand to see the full set.

interface Props {
  wf: WorkflowDetail;
  me: MeResponse | null;
}

// Collapse threshold + initial-visible count. Tuned so that <40 jobs
// always show everything (no surprise hidden rows) but a 100+-month
// batch_export shows a manageable preview by default.
const COLLAPSE_THRESHOLD = 40;
const COLLAPSED_VISIBLE = 25;

interface MergedRow {
  // Stable identifier — actual jobs use their job_id, planned specs
  // use ``planned-<stage>-<position>``.
  key: string;
  source: "actual" | "planned";
  monthLabel: string;
  stage_index: number;
  // Pre-merge sort key — YYYY-MM where available, else falls back to
  // stage_index + position so non-export workflows still order
  // sensibly.
  sortKey: string;
  status: string;
  job?: WorkflowJob;
  spec?: WorkflowPlanSpec;
}

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "success" | "error" | "warning" | "info"
> = {
  planned: "default",
  queued: "info",
  running: "primary",
  succeeded: "success",
  failed: "error",
  cancelled: "warning",
};

const STATUS_ORDER = [
  "running",
  "queued",
  "planned",
  "succeeded",
  "failed",
  "cancelled",
];

export function JobsTable({ wf, me }: Props) {
  const rates = me
    ? { cpuHrUsd: me.cost_cpu_hr_usd, memGibHrUsd: me.cost_mem_gib_hr_usd }
    : null;
  const [plan, setPlan] = useState<WorkflowPlan | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Plan is deterministic for the workflow's params_json, so one fetch
  // covers the page lifetime. Re-fetch on workflow id changes.
  useEffect(() => {
    let cancelled = false;
    getWorkflowPlan(wf.id)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        // Plan unavailable (pre-rc66 operator, or build_stages
        // failed). Fall back to the actual-jobs-only view.
      });
    return () => {
      cancelled = true;
    };
  }, [wf.id]);

  const rows: MergedRow[] = useMemo(
    () => buildMergedRows(wf.jobs, plan),
    [wf.jobs, plan],
  );

  const statusCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) m[r.status] = (m[r.status] ?? 0) + 1;
    return m;
  }, [rows]);

  const collapseEligible = rows.length > COLLAPSE_THRESHOLD;
  const visibleRows =
    collapseEligible && !showAll ? rows.slice(0, COLLAPSED_VISIBLE) : rows;
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography variant="overline">Per-job progress</Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.5}>
          {STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => (
            <Chip
              key={s}
              size="small"
              label={`${statusCounts[s]} ${s}`}
              color={STATUS_COLOR[s] ?? "default"}
              variant={s === "planned" ? "outlined" : "filled"}
              sx={{ height: 22 }}
            />
          ))}
          <Chip
            size="small"
            label={`${rows.length} total`}
            variant="outlined"
            sx={{ height: 22, fontWeight: 600 }}
          />
        </Stack>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>month</TableCell>
            <TableCell>job</TableCell>
            <TableCell>kind</TableCell>
            <TableCell>status</TableCell>
            <TableCell>progress</TableCell>
            <TableCell>message</TableCell>
            <TableCell align="right">est. cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((r) => {
            if (r.source === "planned" || !r.job) {
              return <PlannedRow key={r.key} row={r} />;
            }
            const j = r.job;
            const pct =
              j.progress_total && j.progress_total > 0
                ? Math.round(((j.progress_completed ?? 0) * 100) / j.progress_total)
                : null;
            const cost = rates ? costForJob(j, rates) : 0;
            return (
              <TableRow key={r.key}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{r.monthLabel}</TableCell>
                <TableCell>
                  <code>{j.k8s_job_name ?? j.id.slice(0, 8)}</code>
                  {j.image && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        opacity: 0.55,
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                      }}
                    >
                      {j.image.split(":").pop() ?? j.image}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{j.kind}</TableCell>
                <TableCell>
                  <Chip
                    label={j.status}
                    size="small"
                    color={STATUS_COLOR[j.status] ?? "default"}
                    sx={{ height: 20 }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  {pct !== null ? (
                    <Box>
                      <LinearProgress variant="determinate" value={pct} />
                      <Typography variant="caption">{pct}%</Typography>
                      {(() => {
                        const eta = estimateJobEta(j);
                        if (eta.remainingMs === null) return null;
                        return (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            ~{formatDuration(eta.remainingMs)} left ·{" "}
                            {Math.round(eta.ratePagesPerSec).toLocaleString()} p/s
                          </Typography>
                        );
                      })()}
                    </Box>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {j.progress_message ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="caption"
                    color={cost > 0 ? "text.primary" : "text.disabled"}
                  >
                    {cost > 0 ? formatCost(cost) : "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {collapseEligible && (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <Button size="small" onClick={() => setShowAll((v) => !v)}>
            {showAll
              ? `Show first ${COLLAPSED_VISIBLE}`
              : `Show all ${rows.length} jobs (${hiddenCount} hidden)`}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

function PlannedRow({ row }: { row: MergedRow }) {
  return (
    <TableRow sx={{ opacity: 0.6 }}>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{row.monthLabel}</TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          (not yet submitted)
        </Typography>
      </TableCell>
      <TableCell>{row.spec?.kind ?? "—"}</TableCell>
      <TableCell>
        <Chip label="planned" size="small" variant="outlined" sx={{ height: 20 }} />
      </TableCell>
      <TableCell>—</TableCell>
      <TableCell>—</TableCell>
      <TableCell align="right">—</TableCell>
    </TableRow>
  );
}

// Pull the canonical month label off an actual job row. Mirrors the
// backend's match_key — params.start[:7] when present, else a stage
// label so the column never reads blank.
function jobMonthLabel(j: WorkflowJob): { month: string; sortKey: string } {
  try {
    const p = j.params_json ? JSON.parse(j.params_json) : {};
    const start = typeof p.start === "string" ? p.start : null;
    if (start && start.length >= 7) {
      return { month: start.slice(0, 7), sortKey: start.slice(0, 7) };
    }
  } catch {
    // ignore — fall through to stage fallback
  }
  return {
    month: `stage ${j.stage_index ?? "?"}`,
    sortKey: `zzz-${j.stage_index ?? 0}`,
  };
}

function specMonthLabel(s: WorkflowPlanSpec): { month: string; sortKey: string } {
  if (s.match_key) return { month: s.match_key, sortKey: s.match_key };
  return {
    month: `stage ${s.stage_index}`,
    sortKey: `zzz-${s.stage_index}-${String(s.position).padStart(4, "0")}`,
  };
}

// Merge the plan and the actual job rows into a single list of
// MergedRow, then sort by sortKey. Each plan spec is paired to at
// most one actual job by (stage_index, match_key) — if the operator
// has already submitted the job, the actual row replaces the planned
// pseudo-row.
//
// If the plan is unavailable (operator predates this endpoint, or
// fetch failed), every actual job is still rendered; we just lose
// the "planned" preview rows for not-yet-submitted months.
export function buildMergedRows(
  jobs: WorkflowJob[],
  plan: WorkflowPlan | null,
): MergedRow[] {
  const byKey = new Map<string, WorkflowJob>();
  // Job rows we couldn't pair (no match_key match in the plan) get
  // surfaced anyway at the bottom so nothing is silently hidden.
  const unpairedJobs: WorkflowJob[] = [];

  if (plan) {
    for (const j of jobs) {
      const { month } = jobMonthLabel(j);
      const k = `${j.stage_index ?? 0}::${month}`;
      if (byKey.has(k)) {
        unpairedJobs.push(j);
      } else {
        byKey.set(k, j);
      }
    }
  } else {
    unpairedJobs.push(...jobs);
  }

  const rows: MergedRow[] = [];
  if (plan) {
    for (const spec of plan.specs) {
      const { month, sortKey } = specMonthLabel(spec);
      const k = `${spec.stage_index}::${month}`;
      const job = byKey.get(k);
      if (job) {
        rows.push({
          key: `job-${job.id}`,
          source: "actual",
          monthLabel: month,
          stage_index: spec.stage_index,
          sortKey,
          status: job.status,
          job,
        });
        byKey.delete(k);
      } else {
        rows.push({
          key: `planned-${spec.stage_index}-${spec.position}`,
          source: "planned",
          monthLabel: month,
          stage_index: spec.stage_index,
          sortKey,
          status: "planned",
          spec,
        });
      }
    }
  }

  for (const j of unpairedJobs) {
    const { month, sortKey } = jobMonthLabel(j);
    rows.push({
      key: `job-${j.id}`,
      source: "actual",
      monthLabel: month,
      stage_index: j.stage_index ?? 0,
      sortKey,
      status: j.status,
      job: j,
    });
  }

  rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return rows;
}
